import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { boolToString, parseSelections } from "../DesignUploadStep.functions";
import { Badge } from "@/components/ui/badge";

export type DesignTableCellValue = string | string[] | number | boolean | null | undefined;

export interface DesignTableCellProps {
  value: DesignTableCellValue;
  isEditing: boolean;
  onChange?: (value: string | number | boolean | string[]) => void;
  inputClassName?: string;
  options?: string[];
  inputType?: "text" | "number" | "multiple" | "dropdown";
}

export const DesignTableCell = ({
  value,
  isEditing,
  inputClassName = "h-8 min-w-[120px]",
  inputType = "text",
  options,
  onChange,
}: DesignTableCellProps) => {
  if (isEditing && onChange) {
    if (typeof value === "boolean") {
      return (
        <input
          type="checkbox"
          checked={value}
          className="h-4 w-4 accent-primary"
          onChange={(event) => onChange?.(event.target.checked)}
        />
      );
    }
    if (inputType === "dropdown" && options) {
      return (
        <Combobox
          fullWidth
          className={inputClassName}
          value={typeof value === "string" ? value : undefined}
          onChange={(nextValue) => onChange?.(nextValue)}
          options={options.map((option) => ({ value: option, label: option }))}
        />
      );
    }
    if (inputType === "multiple" && options) {
      return (
        <Combobox
          fullWidth
          multiple
          className={inputClassName}
          value={Array.isArray(value) ? value : []}
          onChange={(nextValue) => onChange?.(nextValue)}
          options={options.map((option) => ({ value: option, label: option }))}
        />
      );
    }
    return (
      <Input
        type={inputType}
        value={value ?? ""}
        className={inputClassName}
        onChange={(event) => {
          if (inputType === "number") {
            const nextValue = event.target.value;
            onChange?.(nextValue === "" ? 0 : Number(nextValue));
            return;
          }

          onChange?.(event.target.value);
        }}
      />
    );
  }

  if (typeof value === "boolean") {
    return boolToString(value);
  }
  if (inputType === "multiple") {
    const parsedValues = Array.isArray(value) ? value : parseSelections(String(value ?? ""));

    if (parsedValues.length === 0) {
      return "—";
    }

    return parsedValues.map((val) => (
      <Badge variant="outline" key={val}>
        {val}
      </Badge>
    ));
  }

  return value ?? "—";
};
